import type {
  IInAppReferralCompaniesResponse,
  IInAppReferralCompanyResponse,
  IInAppReferralLeadsResponse,
  IInAppReferralLeadsSummary,
} from '@commenda/types';
import { fetcher } from '@commenda/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { inAppReferralsKeys } from './keys';
import { IN_APP_REFERRALS_ROUTE } from './routes';
import type {
  AdminInAppReferralCompaniesParams,
  AdminInAppReferralLeadsParams,
  InAppReferralLeadsParams,
} from './types';

const toQuery = (params: Record<string, unknown>): string => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    query.set(key, String(value));
  }
  const serialised = query.toString();
  return serialised ? `?${serialised}` : '';
};

/** Zero, not the workspace default: an admin edits these from another app with its own cache. */
const MONEY_SENSITIVE = { staleTime: 0 } as const;

const getJson = async <T>(path: string, fallback: string): Promise<T> => {
  const res = await fetcher(path, { method: 'GET' });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(body.message || fallback);
  }
  return (await res.json()) as T;
};

export const useInAppReferral = (companyId: number | null | undefined) =>
  useQuery({
    queryKey: inAppReferralsKeys.forCompany(companyId as number),
    queryFn: () =>
      getJson<IInAppReferralCompanyResponse>(
        `${IN_APP_REFERRALS_ROUTE}/company/${companyId}`,
        'Could not load your referral link'
      ),
    enabled: companyId != null && companyId > 0,
    ...MONEY_SENSITIVE,
  });

export const useInAppReferralLeads = (
  params: InAppReferralLeadsParams,
  isEnabled = true
) =>
  useQuery({
    queryKey: inAppReferralsKeys.leadList(params),
    queryFn: () => {
      const { referralId, ...rest } = params;
      return getJson<IInAppReferralLeadsResponse>(
        `${IN_APP_REFERRALS_ROUTE}/${referralId}/leads${toQuery(rest)}`,
        'Could not load your referrals'
      );
    },
    enabled: isEnabled && Boolean(params.referralId),
    placeholderData: keepPreviousData,
    ...MONEY_SENSITIVE,
  });

export const useAdminInAppReferralLeads = (
  params: AdminInAppReferralLeadsParams
) =>
  useQuery({
    queryKey: inAppReferralsKeys.adminLeadList(params),
    queryFn: () =>
      getJson<IInAppReferralLeadsResponse>(
        `${IN_APP_REFERRALS_ROUTE}/leads${toQuery({ ...params })}`,
        'Could not load referral leads'
      ),
    placeholderData: keepPreviousData,
    ...MONEY_SENSITIVE,
  });

export const useAdminInAppReferralSummary = (
  params: AdminInAppReferralLeadsParams
) =>
  useQuery({
    queryKey: inAppReferralsKeys.adminSummary(params),
    queryFn: () =>
      getJson<IInAppReferralLeadsSummary>(
        `${IN_APP_REFERRALS_ROUTE}/leads/summary${toQuery({ ...params })}`,
        'Could not load the referral summary'
      ),
    placeholderData: keepPreviousData,
    ...MONEY_SENSITIVE,
  });

export const useAdminInAppReferralCompanies = (
  params: AdminInAppReferralCompaniesParams
) =>
  useQuery({
    queryKey: inAppReferralsKeys.adminCompanyList(params),
    queryFn: () =>
      getJson<IInAppReferralCompaniesResponse>(
        `${IN_APP_REFERRALS_ROUTE}/company${toQuery({ ...params })}`,
        'Could not load referring companies'
      ),
    placeholderData: keepPreviousData,
    ...MONEY_SENSITIVE,
  });
